<script setup lang="ts">
import { computed } from 'vue';
import CInput from '@/components/shared/CInput';
import CSelect, { type CSelectItem, valueReducer } from '@/components/shared/CSelect';
import CalendarColorField from './CalendarColorField.vue';
import CalendarFontField from './CalendarFontField.vue';
import CalendarStrokeField from './CalendarStrokeField.vue';
import type { CalendarConfig, CalendarStrokeValue, CalendarTextAlign, CalendarTitlePrefix } from './types';

const ALIGN_OPTIONS: CSelectItem<CalendarTextAlign>[] = [
    { label: 'Left', value: 'start' },
    { label: 'Center', value: 'middle' },
    { label: 'Right', value: 'end' }
];

const props = defineProps<{
    config: CalendarConfig;
    prefix: CalendarTitlePrefix;
    disabled?: boolean;
}>();

const emit = defineEmits<{
    (e: 'update', value: Partial<CalendarConfig>): void;
}>();

const keys = computed(() => ({
    font: `${props.prefix}FontPath`,
    size: `${props.prefix}FontSize`,
    margin: `${props.prefix}TopMargin`,
    color: `${props.prefix}Color`,
    strokeWidth: `${props.prefix}StrokeWidth`,
    strokeColor: `${props.prefix}StrokeColor`
} as const));

// The lib renders the combined title always centered, only year/month support alignment
const alignKey = computed(() => (props.prefix === 'yearMonth' ? undefined : (`${props.prefix}Align` as const)));

// Unset alignment falls back to the right side in the lib
const align = computed(() => (alignKey.value ? props.config[alignKey.value] ?? 'end' : undefined));

const update = (key: keyof CalendarConfig, value: unknown) => emit('update', { [key]: value });

const onStrokeUpdate = ({ width, color }: CalendarStrokeValue) => {
    emit('update', { [keys.value.strokeWidth]: width, [keys.value.strokeColor]: color });
};
</script>

<template>
    <div class="calendar-title-style">
        <calendar-font-field :value="config[keys.font]" :disabled="disabled" @input="update(keys.font, $event)" />
        <div class="row">
            <c-input
                :class="alignKey ? 'col-4' : 'col-6'"
                :value="Number(config[keys.size])"
                type="decimal"
                step=".1"
                :min="0"
                label="Font size (pts)"
                :disabled="disabled"
                @input="update(keys.size, String($event))"
            />
            <c-input
                :class="alignKey ? 'col-4' : 'col-6'"
                :value="Number(config[keys.margin])"
                type="decimal"
                step=".1"
                :min="0"
                label="Top Margin"
                :disabled="disabled"
                @input="update(keys.margin, String($event))"
            />
            <c-select
                v-if="alignKey"
                class="col-4"
                :value="align"
                :options="ALIGN_OPTIONS"
                :model-transform="valueReducer"
                :clearable="false"
                label="Horizontal Alignment"
                :disabled="disabled"
                @input="update(alignKey, $event)"
            />
        </div>
        <calendar-color-field
            title="Font Color"
            :value="config[keys.color]"
            :disabled="disabled"
            @input="update(keys.color, $event)"
        />
        <calendar-stroke-field
            :width="config[keys.strokeWidth]"
            :color="config[keys.strokeColor]"
            :disabled="disabled"
            @update="onStrokeUpdate"
        />
    </div>
</template>

<style scoped lang="scss">
.calendar-title-style {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}
</style>
